import { useState, useEffect } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useUser } from "../../context/userContext";

const AddProductModal = ({
  isModalOpen,
  toggleModal,
  categories = [],
  subcategories = [],
  selectedCategory,
  setSelectedCategory,
  selectedSubcategory,
  setSelectedSubcategory,
  navigation,
  fetchProducts,
}) => {
  const [categoryAttributes, setCategoryAttributes] = useState([]);
  const [subcategoryAttributes, setSubcategoryAttributes] = useState([]);
  const [categoryAttributeValues, setCategoryAttributeValues] = useState({});
  const [subcategoryAttributeValues, setSubcategoryAttributeValues] = useState(
    {}
  );
  const [name, setName] = useState("");
  const [details, setDetails] = useState("");
  const [price, setPrice] = useState("");
  const [discount, setDiscount] = useState("");
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user } = useUser();
  const [stock,setStock]=useState("");

  useEffect(() => {
    if (selectedCategory) {
      fetchCategoryAttributes(selectedCategory);
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (selectedSubcategory) {
      fetchSubcategoryAttributes(selectedSubcategory);
    }
  }, [selectedSubcategory]);

  const fetchCategoryAttributes = async (categoryId) => {
    try {
      const response = await axios.get(
        `https://api.theonlysale.com/api/category-attributes/getByCategoryId/${categoryId}`
      );
      if (response.status === 200) {
        const attributesWithDefaults = response.data.map((attribute) => ({
          ...attribute,
          options: attribute.options || [],
        }));
        setCategoryAttributes(attributesWithDefaults);
      } else {
        console.error("Failed to fetch category attributes");
      }
    } catch (error) {
      console.error("Error fetching category attributes:", error);
    }
  };

  const fetchSubcategoryAttributes = async (subcategoryId) => {
    try {
      const response = await axios.get(
        `https://api.theonlysale.com/api/subcategory-attributes/getBySubcategoryId/${subcategoryId}`
      );
      if (response.status === 200) {
        setSubcategoryAttributes(response.data);
      } else {
        console.error("Failed to fetch subcategory attributes");
      }
    } catch (error) {
      console.error("Error fetching subcategory attributes:", error);
    }
  };

  const handleAttributeChange = (type, attributeId, value) => {
    if (type === "category") {
      setCategoryAttributeValues({
        ...categoryAttributeValues,
        [attributeId]: value,
      });
    } else {
      setSubcategoryAttributeValues({
        ...subcategoryAttributeValues,
        [attributeId]: value,
      });
    }
  };

  const handleImageChange = (event) => {
    const files = Array.from(event.target.files);
    setImages(files);
  };

  const handleAddProduct = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      if (
        !name ||
        !details ||
        !price ||
        !selectedCategory ||
        !selectedSubcategory ||
        images.length === 0
      ) {
        toast.error("Please fill all required fields and select images.");
        setLoading(false);
        return;
      }

      const type =
        user.role === "admin" || user.role === "business"
          ? "New"
          : "Second Hand";

      const formData = new FormData();
      formData.append("name", name);
      formData.append("details", details);
      formData.append("price", price);
      formData.append("discount", discount);
      formData.append("category_id", selectedCategory);
      formData.append("subcategory_id", selectedSubcategory);
      formData.append("user_id", user.id);
      formData.append("type", type);
      formData.append("stock", stock);

      for (const [attributeId, value] of Object.entries(
        categoryAttributeValues
      )) {
        formData.append(`categoryAttributes[${attributeId}]`, value);
      }

      for (const [attributeId, value] of Object.entries(
        subcategoryAttributeValues
      )) {
        formData.append(`subcategoryAttributes[${attributeId}]`, value);
      }

      images.forEach((imageFile, index) => {
        formData.append("images", imageFile);
      });

      const response = await axios.post(
        `https://api.theonlysale.com/api/product/add-product`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status >= 200 && response.status < 300) {
        toast.success("Product added successfully.");
        toggleModal();
        fetchProducts();
      }
    } catch (error) {
      console.error("Error adding product:", error);
      toast.error("An error occurred while adding the product.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ToastContainer />
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center w-full h-full overflow-y-auto bg-black bg-opacity-50">
          <div className="relative w-full max-w-2xl p-4 max-h-full">
            <div className="relative p-4 bg-white rounded-lg shadow dark:bg-gray-800 sm:p-5">
              <div className="flex justify-between items-center pb-4 mb-4 rounded-t border-b sm:mb-5 dark:border-gray-600">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Add Product
                </h3>
                <button
                  type="button"
                  onClick={toggleModal}
                  className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center dark:hover:bg-gray-600 dark:hover:text-white"
                >
                  <i className="fa fa-times w-5 h-5"></i>
                  <span className="sr-only">Close modal</span>
                </button>
              </div>
              <form onSubmit={handleAddProduct}>
                <div className="grid gap-4 mb-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="name"
                      className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                      placeholder="Type product name"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="price"
                      className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Price
                    </label>
                    <input
                      type="number"
                      name="price"
                      id="price"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                      placeholder="$2999"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="category"
                      className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Category
                    </label>
                    <select
                      id="category"
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                      required
                    >
                      <option value="" disabled>
                        Select category
                      </option>
                      {categories.map((category) => (
                        <option
                          key={category.category_id}
                          value={category.category_id}
                        >
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="subcategory"
                      className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Subcategory
                    </label>
                    <select
                      id="subcategory"
                      value={selectedSubcategory}
                      onChange={(e) => setSelectedSubcategory(e.target.value)}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                      disabled={!selectedCategory}
                    >
                      <option value="" disabled>
                        Select subcategory
                      </option>
                      {subcategories.map((subcategory) => (
                        <option
                          key={subcategory.subcategory_id}
                          value={subcategory.subcategory_id}
                        >
                          {subcategory.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {categoryAttributes.length > 0 &&
                    categoryAttributes.map((attribute) => (
                      <div key={attribute.attribute_id}>
                        <label
                          htmlFor={`category-attribute-${attribute.attribute_id}`}
                          className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                        >
                          {attribute.name}
                        </label>
                        <input
                          type="text"
                          id={`category-attribute-${attribute.attribute_id}`}
                          value={
                            categoryAttributeValues[attribute.attribute_id] ||
                            ""
                          }
                          onChange={(e) =>
                            handleAttributeChange(
                              "category",
                              attribute.attribute_id,
                              e.target.value
                            )
                          }
                          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                          placeholder={`Enter ${attribute.name}`}
                        />
                      </div>
                    ))}

                  {subcategoryAttributes.length > 0 &&
                    subcategoryAttributes.map((attribute) => (
                      <div key={attribute.attribute_id}>
                        <label
                          htmlFor={`subcategory-attribute-${attribute.attribute_id}`}
                          className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                        >
                          {attribute.name}
                        </label>
                        <input
                          type="text"
                          id={`subcategory-attribute-${attribute.attribute_id}`}
                          value={
                            subcategoryAttributeValues[
                              attribute.attribute_id
                            ] || ""
                          }
                          onChange={(e) =>
                            handleAttributeChange(
                              "subcategory",
                              attribute.attribute_id,
                              e.target.value
                            )
                          }
                          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                          placeholder={`Enter ${attribute.name}`}
                        />
                      </div>
                    ))}

                  <div>
                    <label
                      htmlFor="discount"
                      className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Discount
                    </label>
                    <input
                      type="number"
                      name="discount"
                      id="discount"
                      value={discount}
                      onChange={(e) => setDiscount(e.target.value)}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="stock"
                      className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Stock
                    </label>
                    <input
                      type="number"
                      name="stock"
                      id="stock"
                      value={stock}
                      onChange={(e) => setStock(e.target.value)}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                      placeholder="Enter available stock"
                      required
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label
                      htmlFor="imageURI"
                      className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Images
                    </label>
                    <input
                      type="file"
                      name="imageURI"
                      id="imageURI"
                      onChange={handleImageChange}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                      placeholder="Select image(s)"
                      multiple
                      accept="image/*"
                      required
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label
                      htmlFor="details"
                      className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Details
                    </label>
                    <textarea
                      id="details"
                      rows="4"
                      value={details}
                      onChange={(e) => setDetails(e.target.value)}
                      className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                      placeholder="Provide additional details here"
                    ></textarea>
                  </div>
                </div>

                <button
                  type="submit"
                  className={`text-white inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800 ${
                    loading ? "cursor-wait" : ""
                  }`}
                  disabled={loading}
                >
                  {loading ? (
                    <span>Loading...</span>
                  ) : (
                    <>
                      <i className="fa fa-plus mr-2"></i>
                      <span>Add new product</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddProductModal;
